import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Container } from '@radix-ui/themes'

import { Salenote } from '../interfaces/Salenote'
import useSalenoteFetcher from '../hooks/useSalenoteFetcher'
import { vehicleTypeTranslateEx } from '../utils/vehicleTypeTranslate'
import calcPlateDv from '../utils/calcPlateDv'
import { useAuth } from '../hooks/useAuth'
import { makeImage } from '../utils/makeImage'
import { insertAfterFirstToken } from '../utils/utils'

import DisplayError from './DisplayError'
import { ButtonPrint } from './Buttons'
import { PrintableCard } from './PrintableCard'

const SalenoteResponsability: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { errorSalenote, fetchSalenote } = useSalenoteFetcher()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [salenote, setSalenote] = useState<Salenote>()
  const [vehPlate, setVehPlate] = useState<string>('')
  const { userData } = useAuth()

  const id = searchParams.get('id')

  useEffect(() => {
    fetchDataAsync()
  }, [id])

  const fetchDataAsync = async () => {
    if (!id) {
      return // Exit early if `id` is undefined
    }

    const salenote = await fetchSalenote(id)

    if (errorSalenote) {
      setErrorMessage(errorSalenote.message)

      return
    }

    if (!salenote) {
      setErrorMessage(`Error recuperando la Nota de venta`)

      return
    }
    const plate = salenote?.body.vehicle.plate ?? ''
    const calcPlateDvResult = calcPlateDv(plate)
    let auxPlate

    if (calcPlateDvResult.ok) {
      auxPlate = calcPlateDvResult.plate
    } else {
      setErrorMessage(calcPlateDvResult.msg)
      auxPlate = plate
    }
    setVehPlate(auxPlate)
    setSalenote(salenote)
  }

  const clientName = `${salenote?.body.client.firstname} ${salenote?.body.client.lastname}`
  const clientRut = salenote?.body.client.nin

  const c1 = salenote?.body.client.address
  const c2 = (salenote?.body.client.streetNumber ?? '').toString()
  const c3 = salenote?.body.client.addressComponents ?? ''
  const fullAddress = insertAfterFirstToken(c1, c2, c3)
  const clientAddr = fullAddress

  const clientNameSpan = <span className="font-bold">{clientName}</span>
  const clientAddrSpan = <span className="font-bold">{clientAddr}</span>
  const clientTel = salenote?.body.client.phone
  const clientTelSpan = <span className="font-bold">{clientTel}</span>
  const clientMail = salenote?.body.client.email
  const vehTipo = vehicleTypeTranslateEx(salenote?.body.vehicle.type ?? '')
  const vehColor = salenote?.body.vehicle.color
  const vehChassis = salenote?.body.vehicle.chassisId
  const vehBrand = salenote?.body.vehicle.brand
  const vehModel = `${salenote?.body.vehicle.model} ${salenote?.body.vehicle.version}`
  const vehYear = salenote?.body.vehicle.year
  const vehMotor = salenote?.body.vehicle.engineId
  const today = new Date()
  const day = today.getDate()
  const monthName = today.toLocaleString('es-ES', { month: 'long' })
  const year = today.getFullYear()
  const signatureLine = '____________________________'

  const show = salenote
  const urlImage = userData?.organization?.pictures.logotype

  return (
    <Container size="3" m="4">
      <PrintableCard>
        {errorMessage && <DisplayError msg={errorMessage} />}

        {show && (
          <div>
            <div>
              <div>
                <div className="flex-shrink-0">
                  <img src={makeImage(urlImage ?? '')} alt="Logo" className="h-10" />
                </div>
              </div>

              <div className="flex justify-center text-xl font-bold m-4">DECLARACIÓN DE RESPONSABILIDAD</div>

              <div className="mb-4">
                En SANTIAGO, REPÚBLICA DE CHILE, a <span className="font-bold">{day}</span> de{' '}
                <span className="font-bold">{monthName}</span> de <span className="font-bold">{year}</span>.
              </div>
              <div className="text-justify hyphens-auto">
                <div>
                  Yo, {clientNameSpan} RUT <span className="font-bold">{clientRut}</span> domiciliado para estos efectos
                  en {clientAddrSpan}, teléfono {clientTelSpan}, en mi calidad de comprador y adquiriente DECLARO:
                </div>
                <div>
                  Asumir como de mi entera responsabilidad o cualquier otra persona que lo causare, con o sin mi
                  conocimiento, todo DAÑO y/o prejucio que se ocasione en la persona o bienes de terceros como
                  consecuencia del uso del vehículo mas abajo descrito:
                </div>
              </div>
              <div className="grid grid-cols-6 mt-2 mb-2">
                <div className="col-span-1">Vehículo</div> <div className="font-bold col-span-2">{vehTipo}</div>
                <div className="col-span-1">Año</div>
                <div className="font-bold col-span-2">{vehYear}</div>
                <div className="col-span-1">Marca</div>
                <div className="font-bold col-span-2">{vehBrand}</div>
                <div className="col-span-1">Color</div>
                <div className="font-bold col-span-2">{vehColor}</div>
                <div className="col-span-1">Modelo</div>
                <div className="font-bold col-span-2">{vehModel}</div>
                <div className="col-span-1">N° de Chasis</div>
                <div className="font-bold col-span-2">{vehChassis}</div>
                <div className="col-span-1">Patente</div>
                <div className="font-bold col-span-2">{vehPlate}</div>
                <div className="col-span-1">N° de Motor</div>
                <div className="font-bold col-span-2">{vehMotor}</div>
              </div>
              <div className="text-justify hyphens-auto">
                Declaro, asimismo, como de mi exclusiva responsabilidad, toda infracción a las leyes de tránsito,
                reglamentos vigentes, en relación a este vehículo y también de mi cargo la cancelación de toda multa o
                gravamen, cualquiera sea su origen, naturaleza o autoridad que la imponga, en individualizado,
                obligándome a comparecer ante las autoridades pertinentes. Asi también, como las multas anteriores a la
                compra serán responsabilidad de la automotora.
              </div>

              <div className="mt-20 flex justify-center">
                <div className="flex flex-col items-center">
                  <div>{signatureLine}</div>
                  <div>Firma del Comprador</div>
                  <div>RUT: {clientRut}</div>
                </div>
              </div>

              <div className="flex justify-center mt-10">
                {clientAddr} / {clientMail} / {clientTel}
              </div>
            </div>

            <div className="mt-4">
              <ButtonPrint />
            </div>
          </div>
        )}
      </PrintableCard>
    </Container>
  )
}

export default SalenoteResponsability
